import {
  isCompleteBookingGuestFields,
  isValidBookingGuestEmail,
  toCompleteBookingGuest,
  type CompleteBookingGuest,
} from './resolveBookingGuestIdentity'

const STORAGE_KEY = 'arrival-booking-guest-pending'
const REMEMBERED_EMAIL_KEY = 'arrival-booking-guest-email'

export type { CompleteBookingGuest }

export interface BookingGuestPending {
  email: string
  name?: string
  phone?: string
  profileId?: string
  serviceId?: string
  /** Path + query to resume after email-code auth (e.g. calendar). */
  resumePath: string
  /** `guest` = local identity in storage; Convex guestProfiles row is created on submit.
   * `sign-in` / `sign-up` = email-code Clerk auth; clerkUserId is attached from the Clerk JWT on submit. */
  mode: 'sign-in' | 'sign-up' | 'guest'
}

export function isCompleteGuestSession(
  pending: BookingGuestPending | null,
): pending is BookingGuestPending & CompleteBookingGuest {
  return hasCompleteGuestFields(pending)
}

export function isMagicLinkPendingMode(mode: BookingGuestPending['mode'] | undefined): boolean {
  return mode === 'sign-in' || mode === 'sign-up'
}

export function hasCompleteGuestFields(
  pending: BookingGuestPending | null,
): pending is BookingGuestPending & CompleteBookingGuest {
  return isCompleteBookingGuestFields(pending)
}

export function readCompleteGuestSession(): CompleteBookingGuest | null {
  return toCompleteBookingGuest(readBookingGuestPending())
}

function bookingGuestStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }
  return window.localStorage
}

export function readBookingGuestPending(): BookingGuestPending | null {
  const storage = bookingGuestStorage()
  if (!storage) {
    return null
  }
  const raw = storage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as BookingGuestPending
  } catch {
    return null
  }
}

export function readRememberedGuestEmail(): string | null {
  const storage = bookingGuestStorage()
  if (!storage) {
    return null
  }
  const email = (storage.getItem(REMEMBERED_EMAIL_KEY) ?? sessionStorage.getItem(REMEMBERED_EMAIL_KEY))?.trim()
  if (!email || !isValidBookingGuestEmail(email)) {
    return null
  }
  return email.toLowerCase()
}

export function writeRememberedGuestEmail(email: string): void {
  const storage = bookingGuestStorage()
  const normalized = email.trim().toLowerCase()
  if (!storage || !isValidBookingGuestEmail(normalized)) {
    return
  }
  storage.setItem(REMEMBERED_EMAIL_KEY, normalized)
  sessionStorage.removeItem(REMEMBERED_EMAIL_KEY)
}

export function clearRememberedGuestEmail(): void {
  if (typeof window === 'undefined') {
    return
  }
  localStorage.removeItem(REMEMBERED_EMAIL_KEY)
  sessionStorage.removeItem(REMEMBERED_EMAIL_KEY)
}

export function writeBookingGuestPending(pending: BookingGuestPending): void {
  const storage = bookingGuestStorage()
  if (!storage) {
    return
  }
  storage.setItem(STORAGE_KEY, JSON.stringify(pending))
  sessionStorage.removeItem(STORAGE_KEY)
  writeRememberedGuestEmail(pending.email)
}

export function clearBookingGuestPending(): void {
  if (typeof window === 'undefined') {
    return
  }
  localStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
}
